import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Droplets, Users, PartyPopper, CheckCircle2 } from "lucide-react";
import { api, formatApiError, IMAGES } from "../lib/api";

const eventTypes = ["Mariage", "Anniversaire", "Baptême", "Repas d'entreprise", "Soirée privée", "Concert privé", "Dîner de groupe", "Autre"];

export default function TerracePage() {
  const [settings, setSettings] = useState(null);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "Mariage", date: "", guests: "", message: "" });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/private-requests", { ...form, guests: form.guests ? Number(form.guests) : null, date: form.date || null });
      setDone(true);
      toast.success("Votre demande de devis a bien été envoyée !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="terrace-page">
      <section className="relative min-h-[55vh] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.terrace1} alt="Terrasse avec fontaine" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-black/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 w-full">
          <p className="gold-label mb-3">Un cadre d'exception</p>
          <h1 className="font-serif-elegant text-4xl sm:text-5xl lg:text-6xl font-bold">Terrasse &amp; Événements privés</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="ic-card p-8 text-center">
            <Droplets className="text-[#D4AF37] mx-auto mb-4" size={30} />
            <p className="font-serif-elegant text-2xl font-semibold">Terrasse avec fontaine</p>
            <p className="text-sm text-gray-400 mt-2">Un écrin de verdure et de sérénité</p>
          </div>
          <div className="ic-card p-8 text-center">
            <Users className="text-[#D4AF37] mx-auto mb-4" size={30} />
            <p className="font-serif-elegant text-2xl font-semibold">60 places en terrasse</p>
            <p className="text-sm text-gray-400 mt-2">150 places au total avec la salle intérieure</p>
          </div>
          <div className="ic-card p-8 text-center">
            <PartyPopper className="text-[#D4AF37] mx-auto mb-4" size={30} />
            <p className="font-serif-elegant text-2xl font-semibold">Événements sur mesure</p>
            <p className="text-sm text-gray-400 mt-2">Privatisation totale ou partielle</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <h2 className="font-serif-elegant text-2xl sm:text-3xl font-semibold mb-6">Vos plus beaux moments à l'Italian Club</h2>
            <p className="text-gray-300 leading-relaxed mb-8">{settings?.terrace_text}</p>
            <ul className="grid grid-cols-2 gap-3 text-sm text-gray-300 mb-10">
              {["Mariages", "Anniversaires", "Baptêmes", "Repas d'entreprise", "Soirées privées", "Concerts privés", "Dîners de groupe", "Soirées professionnelles"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-[#009246]" /> {t}</li>
              ))}
            </ul>
            <img src={IMAGES.terrace2} alt="Terrasse Italian Club" className="rounded-2xl border border-white/10 w-full object-cover max-h-[60vh]" />
          </div>

          <form onSubmit={submit} className="ic-card !transform-none p-8" data-testid="private-event-form">
            <h2 className="font-serif-elegant text-2xl font-semibold mb-2">Demander un devis</h2>
            <p className="text-sm text-gray-400 mb-6">Décrivez votre événement, nous vous répondrons sous 48h.</p>
            {done ? (
              <div className="text-center py-10" data-testid="private-request-confirmation">
                <CheckCircle2 size={48} className="text-[#009246] mx-auto mb-4" />
                <p className="text-gray-200">Merci ! Votre demande a bien été envoyée.<br />Notre équipe vous recontactera très vite.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <input data-testid="private-name" required className="ic-input" placeholder="Nom complet *" value={form.name} onChange={set("name")} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="private-email" required type="email" className="ic-input" placeholder="Email *" value={form.email} onChange={set("email")} />
                  <input data-testid="private-phone" required type="tel" className="ic-input" placeholder="Téléphone *" value={form.phone} onChange={set("phone")} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <select data-testid="private-event-type" className="ic-input sm:col-span-1" value={form.event_type} onChange={set("event_type")}>
                    {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input data-testid="private-date" type="date" className="ic-input" value={form.date} onChange={set("date")} />
                  <input data-testid="private-guests" type="number" min="1" className="ic-input" placeholder="Nb d'invités" value={form.guests} onChange={set("guests")} />
                </div>
                <textarea data-testid="private-message" rows="4" className="ic-input" placeholder="Décrivez votre projet…" value={form.message} onChange={set("message")} />
                <button type="submit" data-testid="private-submit-btn" className="btn-gold w-full !py-3.5">Demander un devis</button>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
